import { config } from "../Environment";

export interface TranslationKey {
  _id: string;
  key: string;
  translations: { en?: string | null; de?: string | null; he?: string | null };
  // "auto" = captured automatically from a content field; "manual" = an
  // author explicitly locked a specific term to this key.
  source?: "auto" | "manual";
  // True when the base-language text changed since other-language
  // translations were last written — surfaced on the Translations page so a
  // stale translation is flagged for review instead of silently served.
  possiblyStale?: boolean;
  // Prescription an auto-captured row came from (set once, at creation).
  // Absent for "manual" rows, which aren't tied to one object.
  fiPrescription?: string | null;
  lastUpdated: number;
}

// Regex mirror of backend/utils/resolveTranslationTokens.js's TOKEN_PATTERN —
// used to scan authored content for {{key}} references client-side.
export const TOKEN_PATTERN = /\{\{(\w+)\}\}/g;

export function extractTokenKeys(text: string | null | undefined): string[] {
  if (!text) return [];
  const names = new Set<string>();
  const matches = Array.from(text.matchAll(TOKEN_PATTERN));
  for (let i = 0; i < matches.length; i++) {
    names.add(matches[i][1]);
  }
  return Array.from(names);
}

export const listTranslationKeys = async (
  retryFetch: Function,
  search?: string
): Promise<TranslationKey[]> => {
  const qs = search ? `?search=${encodeURIComponent(search)}` : "";
  const res = await retryFetch(`${config.API_BASE}/api/translation-keys${qs}`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Failed to list translation keys: ${res.statusText}`);
  const body = await res.json();
  return body.data ?? [];
};

export const createTranslationKey = async (
  retryFetch: Function,
  data: { key: string; translations?: Partial<TranslationKey["translations"]> }
): Promise<TranslationKey> => {
  const res = await retryFetch(`${config.API_BASE}/api/translation-keys`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(data),
  });
  const body = await res.json();
  if (!res.ok) {
    const err = new Error(body.message ?? `Failed to create key: ${res.statusText}`) as Error & { status?: number };
    err.status = res.status;
    throw err;
  }
  return body.data;
};

export const updateTranslationKey = async (
  retryFetch: Function,
  id: string,
  data: { key?: string; translations?: Partial<TranslationKey["translations"]> }
): Promise<TranslationKey> => {
  const res = await retryFetch(`${config.API_BASE}/api/translation-keys/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(data),
  });
  const body = await res.json();
  if (!res.ok) throw new Error(body.message ?? `Failed to update key: ${res.statusText}`);
  return body.data;
};

export const deleteTranslationKey = async (retryFetch: Function, id: string): Promise<void> => {
  const res = await retryFetch(`${config.API_BASE}/api/translation-keys/${id}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message ?? `Failed to delete key: ${res.statusText}`);
  }
};

export const suggestKeyTranslation = async (
  retryFetch: Function,
  id: string,
  sourceLang: string,
  targetLang: string
): Promise<string> => {
  const res = await retryFetch(`${config.API_BASE}/api/translation-keys/${id}/suggest`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ sourceLang, targetLang }),
  });
  const body = await res.json();
  if (!res.ok) throw new Error(body.message ?? `Failed to suggest translation: ${res.statusText}`);
  return body.data.translation;
};

export interface BatchTranslationResult {
  id: string;
  key: string | null;
  status: "translated" | "failed";
  translation?: string;
  reason?: string;
}

// Batch-translates up to 25 keys in one AI call, into one target language.
// Unlike suggestKeyTranslation, this DOES persist successful results
// server-side (one bulkWrite) — the caller doesn't need a separate save
// step, just re-fetch the key list to see the new values.
export const suggestKeyTranslationBatch = async (
  retryFetch: Function,
  ids: string[],
  sourceLang: string,
  targetLang: string
): Promise<BatchTranslationResult[]> => {
  const res = await retryFetch(`${config.API_BASE}/api/translation-keys/suggest-batch`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ ids, sourceLang, targetLang }),
  });
  const body = await res.json();
  if (!res.ok) throw new Error(body.message ?? `Failed to batch-suggest translations: ${res.statusText}`);
  return body.data.results;
};

// Translates an ordered list of plain-text segments (e.g. HTML text nodes).
// Does NOT persist anything — used by the frontend HTML skeleton path.
export const suggestTexts = async (
  retryFetch: Function,
  texts: string[],
  sourceLang: string,
  targetLang: string
): Promise<string[]> => {
  const res = await retryFetch(`${config.API_BASE}/api/translation-keys/suggest-texts`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ texts, sourceLang, targetLang }),
  });
  const body = await res.json();
  if (!res.ok) throw new Error(body.message ?? `Failed to suggest text translations: ${res.statusText}`);
  return body.data.translations;
};

export interface SyncAutoCapturedItem {
  tid: string;
  baseLang: "en" | "de" | "he";
  text: string;
}

// Auto-capture translation sync: find-or-create a row per { tid, baseLang,
// text } item. Idempotent — safe to call repeatedly with the same already-
// synced items (a no-op server-side if the text hasn't changed). Called once
// per prescription autosave cycle, fire-and-forget — a failed sync should
// never block or delay the prescription save itself. `fiPrescription`, when
// passed, is stamped onto newly-created rows only (never overwritten on an
// existing row) so the Translations page can scope its "no longer used"
// section to one prescription instead of the author's whole key pool.
export const syncAutoCapturedKeys = async (
  retryFetch: Function,
  items: SyncAutoCapturedItem[],
  fiPrescription?: string
): Promise<TranslationKey[]> => {
  const res = await retryFetch(`${config.API_BASE}/api/translation-keys/sync`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ items, fiPrescription }),
  });
  const body = await res.json();
  if (!res.ok) throw new Error(body.message ?? `Failed to sync translation keys: ${res.statusText}`);
  return body.data.rows;
};
